import { Alert, Modal, Radio, Space, Spin, Typography } from 'antd';
import dayjs from 'dayjs';

import { useSignDocument } from '../hooks';

import type { SignDocumentModalProps } from '../types';
import type { FC } from 'react';

const { Text } = Typography;

const CADES_PLUGIN_URL = 'https://cryptopro.ru/products/cades/plugin';

const formatValidTo = (validTo: string): string =>
  dayjs(validTo).format('DD.MM.YYYY');

export const SignDocumentModal: FC<SignDocumentModalProps> = ({
  documentId,
  role,
  onClose,
  onSigned,
}) => {
  const {
    isOpen,
    isPreparing,
    isPluginMissing,
    isSigning,
    certificates,
    selectedThumbprint,
    setSelectedThumbprint,
    handleSign,
  } = useSignDocument({ documentId, role, onClose, onSigned });

  const hasCertificates = certificates.length > 0;

  return (
    <Modal
      title={role === 'CHIEF' ? 'Подпись организации' : 'Подпись врача'}
      open={isOpen}
      onCancel={onClose}
      onOk={handleSign}
      okText="Подписать"
      cancelText="Отмена"
      okButtonProps={{
        loading: isSigning,
        disabled: isPreparing || isPluginMissing || !selectedThumbprint,
      }}
      destroyOnClose
    >
      {isPreparing && <Spin />}

      {!isPreparing && isPluginMissing && (
        <Alert
          type="error"
          showIcon
          message="Плагин КриптоПро не найден"
          description={
            <>
              Установите КриптоПро CSP и{' '}
              <a href={CADES_PLUGIN_URL} target="_blank" rel="noreferrer">
                браузерный плагин КриптоПро ЭЦП
              </a>
              , затем повторите подписание.
            </>
          }
        />
      )}

      {!isPreparing && !isPluginMissing && !hasCertificates && (
        <Alert
          type="warning"
          showIcon
          message="Действующие сертификаты не найдены"
          description="Проверьте, что сертификат с закрытым ключом установлен и не просрочен."
        />
      )}

      {!isPreparing && !isPluginMissing && hasCertificates && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text>Выберите сертификат подписи:</Text>

          <Radio.Group
            value={selectedThumbprint}
            onChange={(event) => setSelectedThumbprint(event.target.value)}
          >
            <Space direction="vertical">
              {certificates.map((certificate) => (
                <Radio
                  key={certificate.thumbprint}
                  value={certificate.thumbprint}
                >
                  {certificate.name}{' '}
                  <Text type="secondary">
                    (действителен до {formatValidTo(certificate.validTo)})
                  </Text>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Space>
      )}
    </Modal>
  );
};
